import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useCookies } from "react-cookie";

// Hide the browser PDF toolbar (print/download) for every role except Admin and State Admin
const withToolbar = (url, canDownload) =>
  url && !url.includes("#") ? `${url}#toolbar=${canDownload ? 1 : 0}` : url;

const TranslationModal = ({
  open,
  onClose,
  previewUrl,

  // =====================================================
  // EXISTING ACCEPTED TRANSLATION
  // =====================================================
  translationAccepted = false,
  existingTranslationPreviewUrl = "",

  // =====================================================
  // ACTIONS
  // =====================================================
  onRetranslate,
  onAccept,
  onReject,
}) => {
  // =====================================================
  // NEW TRANSLATION PREVIEW
  // =====================================================

  const [previewOpen, setPreviewOpen] = useState(false);
  const [cookies] = useCookies(["permissions"]);
  const canDownload = ["Admin", "State Admin"].includes(cookies.permissions);

  // =====================================================
  // EXISTING TRANSLATION PREVIEW
  // =====================================================

  const [existingPreviewOpen, setExistingPreviewOpen] =
    useState(false);

  // =====================================================
  // LOADING STATES
  // =====================================================

  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [retranslateLoading, setRetranslateLoading] =
    useState(false);

  // =====================================================
  // OPEN PREVIEW
  //
  // If translationAccepted = true:
  //     use existingTranslationPreviewUrl
  //
  // Otherwise:
  //     use previewUrl
  // =====================================================

  const handlePreview = () => {
    const url = translationAccepted
      ? existingTranslationPreviewUrl
      : previewUrl;

    console.log(
      "TRANSLATION PREVIEW URL:",
      url
    );

    if (!url) {
      console.error(
        "Translation preview URL is not available"
      );
      return;
    }

    // =====================================================
    // EXISTING ACCEPTED TRANSLATION
    // =====================================================

    if (translationAccepted) {
      setExistingPreviewOpen(true);
      return;
    }

    // =====================================================
    // NEW TRANSLATION
    // =====================================================

    setPreviewOpen(true);
  };

  // =====================================================
  // CANCEL NEW TRANSLATION PREVIEW
  // =====================================================

  const handlePreviewClose = () => {
    setPreviewOpen(false);

    onClose();
  };

  // =====================================================
  // CANCEL EXISTING TRANSLATION PREVIEW
  //
  // IMPORTANT:
  // This ONLY closes the PDF preview.
  //
  // It does NOT close the
  // "Translation Already Exists" popup.
  //
  // User comes back to:
  //
  // CANCEL | PREVIEW 
  // =====================================================

  const handleExistingPreviewClose = () => {
    setExistingPreviewOpen(false);
  };

  // =====================================================
  // TRANSLATE AGAIN
  // =====================================================

  const handleRetranslate = async () => {
    try {
      setRetranslateLoading(true);

      console.log(
        "Starting re-translation..."
      );

      // Start the new translation
      await onRetranslate();

      // Close the existing translation popup
      onClose();

    } catch (error) {
      console.error(
        "Failed to start re-translation:",
        error
      );
    } finally {
      setRetranslateLoading(false);
    }
  };

  // =====================================================
  // UNSAVE / REJECT NEW TRANSLATION
  // =====================================================

  const handleReject = async () => {
    try {
      setRejectLoading(true);

      // Close PDF preview
      setPreviewOpen(false);

      // Close Translation Ready modal
      onClose();

      await onReject();

    } catch (error) {
      console.error(
        "Failed to reject translation:",
        error
      );
    } finally {
      setRejectLoading(false);
    }
  };

  // =====================================================
  // SAVE / ACCEPT NEW TRANSLATION
  // =====================================================

  const handleAccept = async () => {
    try {
      setAcceptLoading(true);

      // Close PDF preview
      setPreviewOpen(false);

      // Close Translation Ready modal
      onClose();

      await onAccept();

    } catch (error) {
      console.error(
        "Failed to accept translation:",
        error
      );
    } finally {
      setAcceptLoading(false);
    }
  };

  return (
    <>
      {/* =====================================================
          =====================================================
          EXISTING TRANSLATION CONFIRMATION
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          translationAccepted &&
          !existingPreviewOpen
        }
        onClose={
          retranslateLoading
            ? undefined
            : onClose
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Translation Already Exists
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              textAlign: "center",
              mt: 2,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              This document has already been translated.
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: "16px",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              Please preview it before translating again.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            pb: 3,
          }}
        >
          {/* =================================================
              CANCEL
          ================================================= */}

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={retranslateLoading}
          >
            CANCEL
          </Button>

          {/* =================================================
              PREVIEW EXISTING TRANSLATION
          ================================================= */}

          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={
              !existingTranslationPreviewUrl ||
              retranslateLoading
            }
          >
            PREVIEW
          </Button>

          {/* =================================================
              TRANSLATE AGAIN
          ================================================= */}

          {/* <Button
            variant="contained"
            onClick={handleRetranslate}
            disabled={retranslateLoading}
          >
            {retranslateLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                    color: "inherit",
                  }}
                />

                TRANSLATING...
              </>
            ) : (
              "TRANSLATE"
            )}
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          EXISTING TRANSLATION PDF PREVIEW
          =====================================================
          
          IMPORTANT:
          
          This is ONLY for viewing the already accepted
          translation.
          
          There is NO:
          
          SAVE
          UNSAVE
          
          Only:
          
          CANCEL
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          translationAccepted &&
          existingPreviewOpen
        }
        onClose={handleExistingPreviewClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            Existing Translation Preview
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
          }}
        >
          {existingTranslationPreviewUrl ? (
            <iframe
              src={withToolbar(existingTranslationPreviewUrl, canDownload)}
              onContextMenu={(e) => e.preventDefault()}
              title="Existing Translation Preview"
              width="100%"
              height="100%"
              style={{
                border: "none",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">
                Existing translation preview URL is not
                available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleExistingPreviewClose}
          >
            CANCEL
          </Button>
            <Button
    variant="contained"
    onClick={handleRetranslate}
    disabled={retranslateLoading}
  >
    {retranslateLoading ? (
      <>
        <CircularProgress
          size={18}
          sx={{
            mr: 1,
            color: "inherit",
          }}
        />
        TRANSLATING...
      </>
    ) : (
      "RE-TRANSLATE"
    )}
  </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          NEW TRANSLATION READY
          =====================================================
          
          This is shown only when:
          
          translationAccepted = false
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          !translationAccepted &&
          !previewOpen
        }
        onClose={
          acceptLoading || rejectLoading
            ? undefined
            : onClose
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Translation Ready
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              mb: 2,
            }}
          >
            {/* =================================================
                SUCCESS ICON
            ================================================= */}

            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "success.light",
                color: "success.main",
                fontSize: "32px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>

            <Typography
              variant="h6"
              sx={{
                mt: 3,
                fontWeight: 600,
              }}
            >
              Translation Generated Successfully
            </Typography>

            <Typography
              sx={{
                mt: 2,
                color: "text.secondary",
              }}
            >
              The translated document is ready
              for review.
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "text.secondary",
              }}
            >
              Please preview the document before
              accepting the translation.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={!previewUrl}
          >
            PREVIEW
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          NEW TRANSLATION PDF PREVIEW
          =====================================================
          
          This preview has:
          
          CANCEL
          UNSAVE
          SAVE
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          !translationAccepted &&
          previewOpen
        }
        onClose={
          acceptLoading || rejectLoading
            ? undefined
            : handlePreviewClose
        }
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            Translation Preview
          </Typography>
        </DialogTitle>

        {/* =================================================
            PDF
        ================================================= */}

        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
          }}
        >
          {previewUrl ? (
            <iframe
              src={withToolbar(previewUrl, canDownload)}
              onContextMenu={(e) => e.preventDefault()}
              title="Translation Preview"
              width="100%"
              height="100%"
              style={{
                border: "none",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">
                Translation preview URL is not
                available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          {/* =================================================
              CANCEL
          ================================================= */}

          <Button
            variant="outlined"
            onClick={handlePreviewClose}
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            CANCEL
          </Button>

          {/* =================================================
              UNSAVE
          ================================================= */}

          <Button
            variant="outlined"
            color="error"
            onClick={handleReject}
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            {rejectLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                  }}
                />

                DON'T SAVE
              </>
            ) : (
              "DON'T SAVE"
            )}
          </Button>

          {/* =================================================
              SAVE
          ================================================= */}

          <Button
            variant="contained"
            color="success"
            onClick={handleAccept}
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            {acceptLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                    color: "inherit",
                  }}
                />

                SAVING...
              </>
            ) : (
              "SAVE"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TranslationModal;